import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckSquare, DollarSign, Gift, Eye, Globe } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { format, parseISO } from "date-fns";
import { useAdminAuth } from "@/lib/hooks/useAdminAuth";
import {
  getStoreAnalyticsSummary,
  getStoreFootTraffic,
  getStoreAnalyticsCustomers,
  getStoreLoyaltyWatchlist,
} from "@/lib/api/admin";
import type {
  AnalyticsPeriod,
  FootTrafficPoint,
  StoreAnalyticsCustomer,
  LoyaltyWatchlistEntry,
} from "@/lib/api/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/Spinner";
import { SegmentedToggle } from "@/components/SegmentedToggle";
import { formatCents, formatInt } from "@/lib/utils/number";

const PERIOD_OPTIONS: { value: AnalyticsPeriod; label: string }[] = [
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
];

const STATUS_COLORS: Record<string, string> = {
  vip: "#e8231a",
  regular: "#f97316",
  new: "#a3a3a3",
};

type AnalyticsTabProps = {
  storeId: string;
};

export function AnalyticsTab({ storeId }: AnalyticsTabProps) {
  const user = useAdminAuth();
  const [period, setPeriod] = useState<AnalyticsPeriod>("30d");

  const summaryQuery = useQuery({
    queryKey: ["store-analytics-summary", storeId, period],
    queryFn: async () => getStoreAnalyticsSummary(user!, storeId, period),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const footTrafficQuery = useQuery({
    queryKey: ["store-foot-traffic", storeId, period],
    queryFn: async () => getStoreFootTraffic(user!, storeId, period),
    enabled: !!user,
    refetchOnMount: "always",
  });

  // Fixed 100-row sample (not paginated in the UI) for the customer-status pie chart.
  const customersQuery = useQuery({
    queryKey: ["store-analytics-customers", storeId],
    queryFn: async () => getStoreAnalyticsCustomers(user!, storeId, { limit: 100, offset: 0 }),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const watchlistQuery = useQuery({
    queryKey: ["store-loyalty-watchlist", storeId],
    queryFn: async () => getStoreLoyaltyWatchlist(user!, storeId),
    enabled: !!user,
    refetchOnMount: "always",
  });

  const summary = summaryQuery.data;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-end">
        <SegmentedToggle value={period} onChange={setPeriod} options={PERIOD_OPTIONS} />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <KpiCard
          title="Scans"
          value={summary ? formatInt(summary.check_ins) : "—"}
          icon={CheckSquare}
          isLoading={summaryQuery.isPending}
        />
        <KpiCard
          title="Savings Generated"
          value={summary ? formatCents(summary.savings_cents) : "—"}
          icon={DollarSign}
          isLoading={summaryQuery.isPending}
        />
        <KpiCard
          title="Point Rewards Redeemed"
          value={summary ? formatInt(summary.point_rewards_redeemed) : "—"}
          icon={Gift}
          isLoading={summaryQuery.isPending}
        />
        <KpiCard
          title="Website Viewers"
          value={summary ? formatInt(summary.public_profile_views) : "—"}
          icon={Globe}
          isLoading={summaryQuery.isPending}
        />
        <KpiCard
          title="App Viewers"
          value={summary ? formatInt(summary.client_profile_views) : "—"}
          icon={Eye}
          isLoading={summaryQuery.isPending}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <FootTrafficChart
            data={footTrafficQuery.data ?? []}
            isLoading={footTrafficQuery.isPending}
          />
        </div>
        <CustomerBreakdown
          customers={customersQuery.data?.data ?? []}
          isLoading={customersQuery.isPending}
        />
      </div>

      <LoyaltyWatchlist entries={watchlistQuery.data ?? []} isLoading={watchlistQuery.isPending} />
    </div>
  );
}

// ─── Local sub-components (single call site — kept in this file) ──────────────

type KpiCardProps = {
  title: string;
  value: string;
  icon: LucideIcon;
  isLoading?: boolean;
};

function KpiCard({ title, value, icon: Icon, isLoading }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-4 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="min-w-0">
            <p className="text-xs sm:text-sm text-muted-foreground">{title}</p>
            {isLoading ? (
              <div className="mt-2">
                <Spinner className="size-4 border-primary" />
              </div>
            ) : (
              <p className="mt-1 text-xl sm:text-2xl font-bold truncate">{value}</p>
            )}
          </div>
          <div className="hidden sm:flex rounded-md bg-primary/10 p-2 shrink-0 ml-2">
            <Icon className="size-5 text-primary" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

type FootTrafficChartProps = {
  data: FootTrafficPoint[];
  isLoading?: boolean;
};

function FootTrafficChart({ data, isLoading }: FootTrafficChartProps) {
  const formatted = data.map((d) => ({
    date: format(parseISO(d.date), "MMM d"),
    checkIns: d.check_ins,
    publicViews: d.public_profile_views,
    clientViews: d.client_profile_views,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Traffic & Viewers</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="size-6 border-primary" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <LineChart data={formatted}>
              <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
              <YAxis
                tick={{ fontSize: 12 }}
                tickLine={false}
                axisLine={false}
                allowDecimals={false}
              />
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: "0.5rem",
                  border: "1px solid var(--border)",
                }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line
                type="monotone"
                dataKey="checkIns"
                name="Check-ins"
                stroke="var(--primary)"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="publicViews"
                name="Website Viewers"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="clientViews"
                name="App Viewers"
                stroke="#a855f7"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

type CustomerBreakdownProps = {
  customers: StoreAnalyticsCustomer[];
  isLoading?: boolean;
};

function CustomerBreakdown({ customers, isLoading }: CustomerBreakdownProps) {
  const counts = customers.reduce<Record<string, number>>((acc, c) => {
    acc[c.status] = (acc[c.status] ?? 0) + 1;
    return acc;
  }, {});

  const data = Object.entries(counts).map(([status, count]) => ({
    name: status.charAt(0).toUpperCase() + status.slice(1),
    value: count,
    status,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Customer Mix</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex h-48 items-center justify-center">
            <Spinner className="size-6 border-primary" />
          </div>
        ) : data.length === 0 ? (
          <div className="flex h-48 items-center justify-center">
            <p className="text-sm text-muted-foreground">No customer data yet.</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value">
                {data.map((entry) => (
                  <Cell key={entry.status} fill={STATUS_COLORS[entry.status] ?? "#a3a3a3"} />
                ))}
              </Pie>
              <Tooltip
                contentStyle={{
                  fontSize: 12,
                  borderRadius: "0.5rem",
                  border: "1px solid var(--border)",
                }}
              />
              <Legend iconType="circle" iconSize={8} />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
}

type LoyaltyWatchlistProps = {
  entries: LoyaltyWatchlistEntry[];
  isLoading?: boolean;
};

function LoyaltyWatchlist({ entries, isLoading }: LoyaltyWatchlistProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Loyalty Watchlist</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Spinner className="size-6 border-primary" />
          </div>
        ) : entries.length === 0 ? (
          <div className="flex h-32 items-center justify-center px-6">
            <p className="text-sm text-muted-foreground text-center">
              No customers are close to earning a reward right now.
            </p>
          </div>
        ) : (
          <ul className="divide-y">
            {entries.map((entry, i) => (
              <li key={i} className="flex items-center gap-3 px-6 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">
                    {entry.first_name} {entry.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">{entry.reward_name}</p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold">
                    {formatInt(entry.stamps_held)}{" "}
                    <span className="text-muted-foreground font-normal">
                      / {formatInt(entry.stamps_held + entry.stamps_needed)}
                    </span>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {formatInt(entry.stamps_needed)} to go
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}
